import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { organisationsApi } from '~/services/organisations-api/organisationsApi';

import { Organisation, OrganisationsPage } from '~/services/organisations-api/types';

export type OrganisationContextType = {
  organisation: Organisation | undefined;
  organisations: OrganisationsPage | undefined;
  onUpdateActiveOrganisation: (organisationId: string) => Promise<boolean>;
};

export const OrganisationContext = createContext<OrganisationContextType>({
  organisation: undefined,
  organisations: undefined,
  onUpdateActiveOrganisation: async () => false,
});

export type OrganisationProviderProps = {
  children: ReactNode;
};

export function OrganisationProvider({ children }: OrganisationProviderProps) {
  const { data: activeOrg } = useQuery(
    organisationsApi.queryOptions('get', '/active-organisation', {
      params: {
        query: {
          includeBilling: false, // prevents the backend enriching the billing with Stripe data
        },
      },
    })
  );
  const { data: organisations } = useQuery(organisationsApi.queryOptions('get', '/organisations'));
  const queryClient = useQueryClient();

  // Helper to get org query param from URL
  const getOrgSlugFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('org') || null;
  }, []);

  // Helper to check if we're on a route that shouldn't sync org query params
  const isOnAcceptInvitationRoute = useCallback(() => {
    return window.location.pathname.startsWith('/accept-invitation');
  }, []);

  const { mutateAsync: updateActiveOrganisation } = organisationsApi.useMutation(
    'put',
    '/organisations/{organisation_id}/active',
    {
      onSuccess: (org) => {
        queryClient.invalidateQueries();
        const newParams = new URLSearchParams();
        newParams.set('org', org.slug);
        const newUrl = `/?${newParams.toString()}`;
        window.location.href = newUrl;
        return true;
      },
      onError: () => false,
    }
  );

  // Sync query param with API: if org query param is present and different from activeOrg, update active org
  // Skip this logic on /accept-invitation route since the user isn't a member yet
  useEffect(() => {
    // Don't sync org query params on accept-invitation route
    if (isOnAcceptInvitationRoute()) {
      return;
    }

    const orgSlugFromQuery = getOrgSlugFromQuery();

    if (orgSlugFromQuery && activeOrg && organisations?.organisations && activeOrg.slug !== orgSlugFromQuery) {
      // Find the organisation with the slug from query param
      const orgFromQuery = organisations.organisations.find((org) => org.slug === orgSlugFromQuery);

      if (orgFromQuery && orgFromQuery.id !== activeOrg.id) {
        // Update the active organisation to match the query param
        updateActiveOrganisation({
          params: {
            path: {
              organisation_id: orgFromQuery.id,
            },
          },
        }).catch((error) => {
          console.error('Error syncing active organisation with query param:', error);
        });
      }
    }
  }, [activeOrg, organisations, getOrgSlugFromQuery, updateActiveOrganisation, isOnAcceptInvitationRoute]);

  // Determine which organisation to use: query param takes precedence
  // Skip query param lookup on /accept-invitation route since the user isn't a member yet
  const selectedOrganisation = useMemo(() => {
    if (!organisations?.organisations) {
      return activeOrg;
    }

    // Don't try to resolve org from query param on accept-invitation route
    if (isOnAcceptInvitationRoute()) {
      return activeOrg;
    }

    const orgSlugFromQuery = getOrgSlugFromQuery();

    // If org query param is present, find and return that organisation
    if (orgSlugFromQuery) {
      const orgFromQuery = organisations.organisations.find((org) => org.slug === orgSlugFromQuery);
      if (orgFromQuery) {
        return orgFromQuery;
      }
    }

    // Otherwise, fall back to activeOrg from API
    return activeOrg;
  }, [activeOrg, organisations, getOrgSlugFromQuery, isOnAcceptInvitationRoute]);

  const onUpdateActiveOrganisation = useCallback(
    async (organisationId: string) => {
      try {
        await updateActiveOrganisation({
          params: {
            path: {
              organisation_id: organisationId,
            },
          },
        });
        return true;
      } catch (error) {
        console.error('Error updating active organisation:', error);
        return false;
      }
    },
    [updateActiveOrganisation]
  );

  const value = useMemo(
    () => ({ organisation: selectedOrganisation, organisations: organisations, onUpdateActiveOrganisation }),
    [selectedOrganisation, organisations, onUpdateActiveOrganisation]
  );

  return <OrganisationContext.Provider value={value}>{children}</OrganisationContext.Provider>;
}

export function useOrganisation() {
  const { organisation, organisations, onUpdateActiveOrganisation } = useContext(OrganisationContext);

  return { organisation, organisations, onUpdateActiveOrganisation };
}
